'use client';

import { useState } from 'react';

import Link from 'next/link';

import { ArrowRight, CheckCircle } from 'lucide-react';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import { getConfigByProviderAndEnv } from '~/config/billing.config';
import { getFeatures } from '~/config/billing.production.config';
import pathsConfig from '~/config/paths.config';

type Interval = 'month' | 'year';

const CUSTOM_FEATURES = [
  'Everything in Enterprise',
  'Custom computing power quota',
  'Dedicated infrastructure',
  'SLA guarantee',
  'Custom integrations',
  'Priority support & onboarding',
  'Custom contract & invoicing',
];

export function MarketingPricingTable() {
  const [interval, setInterval] = useState<Interval>('month');

  const config = getConfigByProviderAndEnv();
  const starterProduct = config.products.find((p) => p.id === 'starter')!;
  const enterpriseProduct = config.products.find((p) => p.id === 'enterprise')!;

  const starterPlan = starterProduct.plans.find(
    (p) => p.paymentType === 'recurring' && p.interval === interval,
  )!;

  const enterprisePlan = enterpriseProduct.plans.find(
    (p) => p.paymentType === 'recurring' && p.interval === interval,
  )!;

  const starterPrice = starterPlan?.lineItems[0]?.cost ?? 0;
  const enterprisePrice = enterprisePlan?.lineItems[0]?.cost ?? 0;

  const billingInterval = interval === 'month' ? 'monthly' : 'yearly';

  return (
    <div className={'flex flex-col space-y-8 xl:space-y-12'}>
      {/* Interval switcher */}
      <div className={'flex justify-center'}>
        <div className={'flex gap-x-1 rounded-full border p-1.5'}>
          {(['month', 'year'] as Interval[]).map((i) => (
            <Button
              key={i}
              size={'sm'}
              variant={interval === i ? 'default' : 'ghost'}
              className={'rounded-full'}
              onClick={() => setInterval(i)}
            >
              {i === 'month' ? 'Monthly' : 'Yearly'}
            </Button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div
        className={
          'flex flex-col items-start space-y-6 lg:space-y-0' +
          ' justify-center lg:flex-row lg:space-x-4'
        }
      >
        {/* Starter */}
        <PricingCard
          name="Starter"
          badge="Value"
          price={starterPrice}
          interval={interval}
          description={starterProduct.description}
          features={getFeatures('starter', billingInterval)}
          ctaLabel="Get Started"
          ctaHref={`${pathsConfig.auth.signUp}?plan=${starterPlan?.id}&next=${pathsConfig.app.home}`}
        />

        {/* Enterprise */}
        <PricingCard
          name="Enterprise"
          price={enterprisePrice}
          interval={interval}
          description={enterpriseProduct.description}
          features={getFeatures('enterprise', billingInterval)}
          highlighted
          ctaLabel="Get Started"
          ctaHref={`${pathsConfig.auth.signUp}?plan=${enterprisePlan?.id}&next=${pathsConfig.app.home}`}
        />

        {/* Custom */}
        <PricingCard
          name="Custom"
          customPrice="Custom pricing"
          description="Tailored for large-scale institutions with unique requirements"
          features={CUSTOM_FEATURES}
          ctaLabel="Contact Us"
          ctaHref="/contact"
        />
      </div>
    </div>
  );
}

function PricingCard({
  name,
  badge,
  price,
  customPrice,
  interval,
  description,
  features,
  highlighted = false,
  ctaLabel,
  ctaHref,
}: {
  name: string;
  badge?: string;
  price?: number;
  customPrice?: string;
  interval?: Interval;
  description: string;
  features: string[];
  highlighted?: boolean;
  ctaLabel: string;
  ctaHref: string;
}) {
  return (
    <div
      className={cn(
        's-full relative flex flex-1 grow flex-col items-stretch justify-between self-stretch rounded-lg border px-6 py-5 lg:w-4/12 xl:max-w-[20rem]',
        {
          'border-primary': highlighted,
          'border-border': !highlighted,
        },
      )}
    >
      {badge && (
        <div className={'absolute -top-2.5 left-0 flex w-full justify-center'}>
          <Badge
            className={highlighted ? '' : 'bg-background'}
            variant={highlighted ? 'default' : 'outline'}
          >
            {badge}
          </Badge>
        </div>
      )}

      <div className={'flex flex-col gap-y-5'}>
        {/* Name */}
        <div className={'flex flex-col gap-y-1'}>
          <b
            className={
              'text-secondary-foreground font-heading text-xl font-medium tracking-tight'
            }
          >
            {name}
          </b>
        </div>

        {/* Price */}
        <div className={'mt-6 flex flex-col gap-y-1'}>
          <div
            className={
              'animate-in slide-in-from-left-4 fade-in flex items-end gap-1 duration-500'
            }
          >
            <span
              className={
                'font-heading flex items-center text-4xl font-medium tracking-tighter'
              }
            >
              {customPrice ? (
                <span className={'text-2xl'}>{customPrice}</span>
              ) : (
                <>
                  <span className={'text-2xl'}>$</span>
                  {interval === 'year'
                    ? Math.round((price ?? 0) / 12)
                    : price}
                </>
              )}
            </span>

            {!customPrice && interval && (
              <span className={'text-muted-foreground text-sm leading-loose'}>
                /mo
              </span>
            )}
          </div>

          {!customPrice && interval && (
            <span
              className={
                'text-muted-foreground animate-in slide-in-from-left-4 fade-in text-xs capitalize'
              }
            >
              {interval === 'month' ? 'Billed monthly' : 'Billed yearly'}
            </span>
          )}
        </div>

        {/* CTA */}
        <Link className={'w-full'} href={ctaHref}>
          <Button
            size={'lg'}
            className={'h-12 w-full rounded-lg'}
            variant={highlighted ? 'default' : 'secondary'}
          >
            <span className={'text-base font-medium tracking-tight'}>
              {ctaLabel}
            </span>
            <ArrowRight className={'ml-2 h-4'} />
          </Button>
        </Link>

        {/* Description */}
        <span className={cn('text-muted-foreground text-base tracking-tight')}>
          {description}
        </span>

        <div className={'h-px w-full border border-dashed'} />

        {/* Features */}
        <ul className={'flex flex-col gap-1'}>
          {features.map((feature) => (
            <li key={feature} className={'flex items-center gap-x-2.5'}>
              <CheckCircle
                className={cn('h-4 min-h-4 w-4 min-w-4', {
                  'text-secondary-foreground': highlighted,
                  'text-muted-foreground': !highlighted,
                })}
              />
              <span
                className={cn('text-sm', {
                  'text-muted-foreground': !highlighted,
                  'text-secondary-foreground': highlighted,
                })}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
